import React, { FC, useEffect, lazy, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { faGear } from '@fortawesome/pro-solid-svg-icons';

import { IState } from '../../../reducers';
import { IProfileReducer } from '../../../reducers/profileReducer';
import { isPlatformAdmin } from '../../../tools/permissions';
import { EPlatformPermissions } from '../../../entities/IPermissions';
import { Loader } from '../../Common/Loader/Loader';
import { MainWrapper } from '../../Common/MainWrapper/MainWrapper';
import lazyBlock from '../../../tools/lazyBlock';
import { SingleTab, Tabs } from '../../Common/Tabs/Tabs';
import { MainTitle } from '../../Common/MainTitle/MainTitle';
import { ILanguageReducer } from '../../../reducers/languageReducer';
import IntlMessage, { useIntlMessage } from '../../Common/IntlMessage';
import { ADMIN_TAB_DEFAULT_MESSAGES, EAdminTab, isSuperAdminOnlyTab } from './adminTabs';

export { EAdminTab, isSuperAdminOnlyTab } from './adminTabs';

const content = {
    [EAdminTab.ORGANIZATIONS]: lazyBlock(lazy(() => import('./V2/Workspaces'))),
    [EAdminTab.REFERENTIALS]: lazyBlock(lazy(() => import('./Datagrids/ReferentialsColoredDatagrid'))),
    [EAdminTab.TRANSLATIONS]: lazyBlock(lazy(() => import('./Datagrids/TranslationColoredDatagrid'))),
    [EAdminTab.USERS]: lazyBlock(lazy(() => import('./V2/Members'))),
    [EAdminTab.USERSNEW]: lazyBlock(lazy(() => import('./V2/Users'))),
    [EAdminTab.INVITATIONS]: lazyBlock(lazy(() => import('./V2/Invitations'))),
    [EAdminTab.GROUPS]: lazyBlock(lazy(() => import('./V2/Groups/Groups'))),
    [EAdminTab.USEFULLINKS]: lazyBlock(lazy(() => import('./Datagrids/UsefulLinksDataTable'))),
    [EAdminTab.WORKSPACEINVITATION]: lazyBlock(lazy(() => import('./V2/WorkspaceInvitation'))),
    [EAdminTab.CONFIGURATION]: lazyBlock(lazy(() => import('./PlatformConfiguration/PlatformConfiguration')))
};

export const AdminConsoleContainerV2: FC<React.PropsWithChildren<unknown>> = () => {
    const { intlFormatMessage } = useIntlMessage();
    let location = useLocation();
    const history = useNavigate();
    const { currentUserPermissions, isSuperAdmin, translations } = useSelector<IState, IProfileReducer & ILanguageReducer>(state => ({
        ...state.profile,
        ...state.language
    }));
    const [currentTab, setCurrentTab] = useState<string>(EAdminTab.USERS);
    const PageContent = content[currentTab || EAdminTab.USERS];

    useEffect(() => {
        const stringWithoutHash = location.hash?.substring(1);
        setCurrentTab(stringWithoutHash.toUpperCase() || EAdminTab.USERS);
        return () => {
            setCurrentTab(EAdminTab.USERS);
        };
    }, []);

    const changeTab = (tab: EAdminTab) => {
        history(`/admin-console#${tab}`);
        setCurrentTab(tab.toUpperCase() || EAdminTab.USERS);
    };

    return (
        <MainWrapper permissionContext={EPlatformPermissions.PlatformManagementView}>
            <MainTitle
                icon={faGear}
                text={intlFormatMessage({ id: 'global.settings' })}
            />
            <Loader loading={!currentUserPermissions && Object.keys(translations)?.length === 0}>
                {isPlatformAdmin(currentUserPermissions) ? (
                    <>
                        <Tabs>
                            {(Object.keys(EAdminTab) as (keyof typeof EAdminTab)[])
                                ?.filter(tab => !isSuperAdminOnlyTab(tab) || isSuperAdmin)
                                .map(tab =>
                                    <SingleTab
                                        key={tab}
                                        active={currentTab === tab}
                                        onClick={() => changeTab(tab.toLowerCase() as EAdminTab)}
                                    >
                                        <IntlMessage
                                            id={`admin.${tab.toLowerCase()}`}
                                            defaultMessage={ADMIN_TAB_DEFAULT_MESSAGES[tab]}
                                        />
                                    </SingleTab>
                                )}
                        </Tabs>
                        {isSuperAdminOnlyTab(currentTab) && !isSuperAdmin
                            ? <p><IntlMessage id="admin.noPerm" /></p>
                            : <PageContent />
                        }
                    </>
                ) : (
                    <p><IntlMessage id="admin.noPerm" /></p>
                )}
            </Loader>
        </MainWrapper>
    );
};

export default AdminConsoleContainerV2;